<template>
  <div class="flex w-full">
    <input
      type="file"
      ref="uploader"
      @change="handleFile"
      multiple
      :accept="acceptedFileFormats"
      class="hidden"
    />
    <div class="w-full">
      <div class="flex flex-col w-full">
        <div
          class="w-full group"
          :class="[
            {
              'cursor-no-drop opacity-60': disable,
              'cursor-pointer': !disable,
            },
          ]"
          @click="
            () => {
              pickFile();
            }
          "
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @dragenter.prevent
          @drop.prevent="
            (e) => {
              handleFile(e, true);
              isDragging = false;
            }
          "
        >
          <div class="flex flex-col items-center w-full h-full justify-center">
            <MeridiaIcon
              class="icon-m transition-all duration-300"
              :class="{
                'text-neutral-text-body': !isDragging,
                'text-primary-surface-default': isDragging,
                'group-hover:text-primary-surface-default': !disable,
              }"
              name="cloud_upload"
              type="material-icons-outlined"
            />
            <transition name="fade" mode="out-in">
              <div
                v-if="!isDragging"
                class="text-neutral-text-body text-body-large transition-all mt-2"
              >
                {{
                  $t(
                    'meridia_components.file_upload.select_or_drag_and_drop_files_to_upload'
                  )
                }}
              </div>
              <div
                v-else
                class="text-neutral-text-body text-body-large transition-all mt-2"
              >
                {{ $t('meridia_components.file_upload.drop_files_here') }}
              </div>
            </transition>
            <div class="text-neutral-border-disabled text-body-small mt-xs">
              {{
                $t('meridia_components.file_upload.upload_subtitle', {
                  formats: acceptedFileFormats,
                  limit: maxFileLimit / 1024 / 1024,
                })
              }}
            </div>
            <MeridiaButton
              :type="'tertiary'"
              :size="'base'"
              :label="$t('meridia_components.file_upload.upload_file')"
              class="mt-2xs"
            />
          </div>
        </div>
      </div>
      <transition-group
        name="fade"
        mode="out-in"
        :duration="150"
        tag="div"
        class="gap-y-xs flex"
      >
        <div
          v-for="fileObject in files"
          :key="fileObject.id"
          class="p-3 w-full flex justify-center items-center radius-base bg-white"
        >
          <div class="inline-flex justify-between w-full items-center">
            <div class="inline-flex items-center mr-xs flex-1 overflow-hidden">
              <transition name="fade" mode="out-in" :duration="150">
                <MeridiaIcon
                  v-if="fileObject.status === 'success'"
                  class="icon-s text-primary-surface-default p-xs"
                  name="check"
                  type="material-icons-outlined"
                />
                <MeridiaIcon
                  v-else-if="fileObject.status === 'error'"
                  class="icon-s text-critical-surface-default p-xs"
                  name="block"
                  type="material-icons-outlined"
                />
                <PrimeProgressSpinner v-else class="w-4 h-4 p-xs" />
              </transition>
              <span class="text-neutral-text-body text-body-base truncate">{{
                fileObject.file.name
              }}</span>
              <div class="h-m w-px bg-neutral-border-subtle mx-2xs" />
              <span
                v-if="fileObject.status === 'success'"
                class="text-neutral-border-disabled text-body-base"
                >{{ humanStorageSize(fileObject.file.size) }}</span
              >
              <span
                v-else-if="fileObject.status === 'error'"
                class="text-critical-label-default text-body-base whitespace-nowrap"
                >{{ humanStorageSize(fileObject.file.size) }} •
                {{
                  fileObject.reason
                    ? $t('meridia_components.file_upload.' + fileObject.reason)
                    : $t('meridia_components.file_upload.upload_failed')
                }}</span
              >
            </div>
            <div class="inline-flex items-center">
              <MeridiaButton
                v-if="fileObject.status === 'success'"
                :type="'tertiary'"
                :size="'base'"
                icon="open_in_new"
                @click.prevent="handleDownload(fileObject)"
              />
              <MeridiaButton
                v-if="fileObject.status !== 'pending'"
                :type="'tertiary'"
                :size="'base'"
                @click.prevent="deleteFile($event, fileObject)"
              >
                <template #customIcon>
                  <MeridiaIcon
                    name="delete"
                    type="material-symbols-outlined"
                    style="font-size: inherit"
                  />
                </template>
              </MeridiaButton>
            </div>
          </div>
        </div>
      </transition-group>
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { MeridiaFileType } from '.';
import MeridiaButton from '../button/MeridiaButton.vue';
import PrimeProgressSpinner from 'primevue/progressspinner';
import MeridiaIcon from '../icon/MeridiaIcon.vue';

/**
 * Holds the selected files for upload.
 * @type {Ref<MeridiaFileType>}
 */
const files = defineModel<MeridiaFileType[]>({ required: true });

const props = withDefaults(
  defineProps<{
    acceptedFileFormats: string;
    maxFileLimit?: number | undefined;
    disable?: boolean;
  }>(),
  {
    maxFileLimit: 5368709120,
  }
);

const emit = defineEmits<{
  (
    event: 'createFile',
    file: File,
    callback: (response: {
      status: boolean;
      reason: string | undefined;
      id: number | undefined;
    }) => Promise<void>
  ): void;
  (
    event: 'deleteFile',
    fileObject: MeridiaFileType,
    callback: (response: boolean) => Promise<void>
  ): void;
  (
    event: 'downloadFile',
    fileObject: MeridiaFileType,
    callback: (response: boolean) => Promise<void>
  ): void;
}>();

const isDragging = ref(false);

/**
 * Holds the uploader instance.
 * @type {Ref<any>}
 */
const uploader = ref<any>(null);

/**
 * Function to open the file picker dialog.
 */
function pickFile() {
  if (props.disable) return;
  uploader.value.click();
}

function checkSizeValid(auxFiles: FileList) {
  const MAX_SIZE = props.maxFileLimit;
  const EMPTY = 0;
  const filesFormatted = Array.from(auxFiles);

  return filesFormatted.some((file) => {
    if (file.size === EMPTY) {
      files.value.push({
        status: 'error',
        reason: 'empty',
        id: Math.random() * 1000,
        file: file,
      });
      return true;
    } else if (file.size > MAX_SIZE) {
      files.value.push({
        status: 'error',
        reason: 'too_big',
        id: Math.random() * 1000,
        file: file,
      });
      return true;
    } else {
      return false;
    }
  });
}
/**
 * Function to handle the selected file(s) and update the files list.
 * @param {Event} event - The file input change event.
 */
async function handleFile(event: Event, isDrag = false) {
  let eventFiles: { id: number; file: File }[];
  if (isDrag) {
    // convert current files into allFiles array to be able to add new files
    if (!(event as DragEvent)?.dataTransfer?.files) return;

    // add new files to allFiles array
    eventFiles = Array.from(
      (event as DragEvent).dataTransfer?.files as FileList
    ).map((file) => {
      const id = Math.random() * 1000;
      return {
        id: id,
        file: file,
      };
    });
  } else {
    const targetEvent = event.target as HTMLInputElement;
    // convert current files into allFiles array to be able to add new files
    if (!targetEvent.files) return;

    if (targetEvent.files !== null && checkSizeValid(targetEvent.files)) {
      return;
    }
    // add new files to allFiles array
    eventFiles = Array.from(
      (event.target as HTMLInputElement).files as FileList
    ).map((file) => {
      const id = Math.random() * 1000;
      return {
        id: id,
        file: file,
      };
    });
  }

  eventFiles.map(async (fileObject) => {
    files.value.push({
      status: 'pending',
      reason: '',
      id: fileObject.id,
      file: fileObject.file,
    });
  });
  // start new files upload
  await Promise.all(
    eventFiles.map(async (fileObject) => {
      await new Promise((resolve) => {
        emit('createFile', fileObject.file, async (response) => {
          const index = files.value.findIndex((f) => f.id === fileObject.id);
          files.value[index] = {
            status: response.status ? 'success' : 'error',
            reason: response.reason,
            id: response.id ?? Math.random() * 1000,
            file: fileObject.file,
          };
          resolve(true);
        });
      });
    })
  ).then(() => {
    if (uploader.value) {
      uploader.value.value = '';
    }
  });
}

function handleDownload(fileObject: MeridiaFileType) {
  emit('downloadFile', fileObject, async (response) => {
    if (!response) {
      const index = files.value.findIndex((f) => f.id === fileObject.id);
      if (index !== -1) {
        files.value[index].status = 'error';
        files.value[index].reason = 'download_failed';
      }
    }
  });
}

/**
 * Function to delete a file from the files list.
 * @param {File} file - The file to be deleted.
 * @param {Boolean} failedUpload - failed upload or not.
 */
async function deleteFile(
  e: Event,
  fileObject: MeridiaFileType,
  failedUpload = false
) {
  e.stopPropagation();
  if (!files.value) return;
  if (!failedUpload || fileObject.reason === 'delete_failed') {
    emit('deleteFile', fileObject, async (response) => {
      if (response) {
        const index = files.value.findIndex((f) => f.id === fileObject.id);
        if (index > -1) {
          files.value.splice(index, 1);
        }
      } else {
        const index = files.value.findIndex((f) => f.id === fileObject.id);
        files.value[index].status = 'error';
        files.value[index].reason = 'delete_failed';
      }
    });
  } else {
    const index = files.value.findIndex((f) => f.id === fileObject.id);
    if (index > -1) {
      files.value.splice(index, 1);
    }
  }
}

function humanStorageSize(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i]
  );
}
</script>
